import type { components } from "@appretio/api";
import { useApi } from "@appretio/api";
import { useQuery } from "@tanstack/react-query";
import type { StackedChartDataPoint } from "../utils/stackedTransforms";
import { stackedChartQueryKeys } from "./queryKeys";

/**
 * Parameters for fetching stacked chart data from the API.
 *
 * ``cubeId`` carries either a DimCube UUID (user/budget/other-user cubes)
 * or a DS version string (analytics); the server resolves the producer.
 */
interface StackedDataParams {
  context: Record<string, unknown>;
  cubeId: string;
  dimension: string;
  metricName: string;
  fiscalYear: string;
}

export function useFetchStackedData(
  params: StackedDataParams | null,
  enabled: boolean
) {
  const apiClient = useApi();

  return useQuery({
    queryKey: stackedChartQueryKeys.data(
      (params ?? {}) as Record<string, unknown>
    ),
    meta: {
      errorMessage: "Failed to fetch stacked chart data"
    },
    queryFn: async (): Promise<StackedChartDataPoint[]> => {
      if (!params) return [];
      const response = await apiClient.POST("/v2/forecast/charts/stacked", {
        body: {
          context:
            params.context as components["schemas"]["ForecastContextOptions-Input"],
          cube_id: params.cubeId,
          dimension: params.dimension,
          metric_name: params.metricName,
          fiscal_year: params.fiscalYear
        }
      });
      const result = response.data?.result?.data;
      if (!result) {
        throw new Error("Missing result in stacked chart response");
      }
      return result;
    },
    staleTime: 60_000,
    enabled: enabled && params !== null
  });
}
